import { motion } from 'framer-motion';
import { Check, BarChart3, Shield, Zap, Sparkles, Share2, FileLineChart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const ProductPage = () => {
  const features = [
    {
      title: 'Scope 1 & 2 Tracking',
      description: 'Easily log Electricity, Gas, Fuel, and other Scope 1 and 2 emissions data with automatic CO₂-e calculations.',
      icon: Zap
    },
    {
      title: 'Visual Dashboard',
      description: 'Dynamic charts and tiles provide instant visibility into your carbon footprint across different financial years.',
      icon: BarChart3
    },
    {
      title: 'AASB S2 Reporting',
      description: 'Generate professional reports aligned with Australian Sustainability Reporting Standards and QPP requirements.',
      icon: FileLineChart
    },
    {
      title: 'AI ESG Copilot',
      description: 'Get instant answers to complex ESG questions and narrative assistance for your reports with our in-built AI assistant.',
      icon: Sparkles
    },
    {
      title: 'Team Collaboration',
      description: 'Invite team members to view or maintain data with granular RBAC permissions (Owner, Editor, Viewer).',
      icon: Share2
    },
    {
      title: 'Data Integrity',
      description: 'Upload evidence and receipts directly to entries for a robust audit trail and verified compliance.',
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="product-page">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col items-center text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-sm font-medium tracking-wide uppercase text-[#74A57F] bg-[#74A57F]/10 px-4 py-2 rounded-full mb-6"
            >
              The Next Generation of Carbon Compliance
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold text-[#003B5C] mb-8 leading-tight max-w-4xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Emissions Tracker <span className="text-[#74A57F]">Dashboard</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-600 mb-10 max-w-2xl leading-relaxed"
            >
              Track, visualise, and report your carbon footprint with ease. Built specifically for SMEs to navigate the complexity of Australian ESG standards.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <Button asChild size="lg" className="bg-[#003B5C] hover:bg-[#002B45] text-white px-8 h-14 rounded-full text-lg font-medium shadow-xl">
                <Link to="/signup">Get Started for Free <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-slate-200 hover:bg-slate-50 px-8 h-14 rounded-full text-lg font-medium">
                <Link to="/faq">View Pricing & FAQs</Link>
              </Button>
            </motion.div>
          </div>

          {/* Product Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.4, type: 'spring', damping: 20 }}
            className="relative px-4"
          >
            <div className="relative bg-white rounded-3xl shadow-[0_40px_100px_rgba(0,59,92,0.15)] p-4 border border-slate-100 max-w-5xl mx-auto overflow-hidden">
              <img
                src="/lucent_sky_dashboard_mockup.png"
                alt="Lucent Sky ESG Dashboard"
                className="w-full h-auto rounded-2xl"
                data-testid="product-hero-image"
              />

              {/* Glassmorphic decorative elements */}
              <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#74A57F]/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 w-96 h-96 bg-[#003B5C]/10 rounded-full blur-3xl pointer-events-none" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything you need for ESG Compliance
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our platform simplifies the complex world of carbon reporting into a seamless, automated workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="p-8 rounded-2xl border border-slate-100 bg-[#F8F9FA] hover:bg-white hover:shadow-xl transition-all duration-300"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[#003B5C] rounded-xl flex items-center justify-center mb-6 text-white shadow-lg shadow-[#003B5C]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built for Standards Section */}
      <section className="py-24 bg-[#003B5C] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0 100 C 20 0 50 0 100 100" fill="none" stroke="white" strokeWidth="0.1" />
            <path d="M0 80 C 30 20 60 20 100 80" fill="none" stroke="white" strokeWidth="0.1" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Aligned with Modern Australian Standards
              </h2>
              <p className="text-xl text-white/80 mb-10 leading-relaxed">
                We stay ahead of the regulatory curve so you don't have to. Our calculations are automatically updated to match the latest NGA factors.
              </p>

              <ul className="space-y-6">
                <li className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-[#74A57F] rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">ASRS & AASB S2</h4>
                    <p className="text-white/70">Full alignment with Australian Sustainability Reporting Standards.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-[#74A57F] rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">QPP 2026 Ready</h4>
                    <p className="text-white/70">Meet the requirements for Queensland Government procurement tenders.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-[#74A57F] rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">2032 Olympics Compliance</h4>
                    <p className="text-white/70">Position your business for major infrastructure and supplier opportunities.</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">Join the Future of ESG</h3>
                <p className="text-white/60 text-sm">Empower your business with data-driven sustainability.</p>
              </div>

              <div className="space-y-4">
                <div className="bg-white/10 p-6 rounded-2xl flex items-center justify-between border border-white/5">
                  <div>
                    <div className="text-sm text-white/60 mb-1 uppercase tracking-wider font-medium">Standard Plan</div>
                    <div className="text-2xl font-bold">From $11 <span className="text-sm font-normal text-white/40">/ mo</span></div>
                  </div>
                  <Button asChild variant="secondary" className="bg-white text-[#003B5C] hover:bg-slate-100 rounded-full">
                    <Link to="/signup">Choose Standard</Link>
                  </Button>
                </div>

                <div className="bg-white/10 p-6 rounded-2xl flex items-center justify-between border border-[#74A57F]/50 ring-1 ring-[#74A57F]/50">
                  <div>
                    <div className="text-sm text-[#74A57F] mb-1 uppercase tracking-wider font-bold">Pro Plan</div>
                    <div className="text-2xl font-bold">From $22 <span className="text-sm font-normal text-white/40">/ mo</span></div>
                  </div>
                  <Button asChild className="bg-[#74A57F] hover:bg-[#638e6d] text-white rounded-full">
                    <Link to="/signup">Choose Pro</Link>
                  </Button>
                </div>

                <p className="text-center text-xs text-white/40 pt-4">
                  No hidden costs. 20% discount applies for additional organisations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to start your sustainability journey?
          </h2>
          <p className="text-xl text-slate-600 mb-12 leading-relaxed">
            Create your account in minutes and get an instant overview of your carbon footprint. No credit card required to get started.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button asChild size="lg" className="bg-[#003B5C] hover:bg-[#002B45] text-white px-10 h-16 rounded-full text-xl font-medium shadow-xl">
              <Link to="/signup">Get Started Now</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-slate-200 hover:bg-slate-50 px-10 h-16 rounded-full text-xl font-medium">
              <Link to="/contact">Talk to an Expert</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
